import { Button, Modal } from 'antd';
import { PureComponent, ReactNode } from 'react';
import { cloneAndUpsert, getUnit } from '../../../../../store/utils';
import { WarningIcon } from '../../../../common/Icons';
import AddDrugDialog from '../AddDrugDialog';
import fetchDrugSearch, { CodedMedications } from '../../../../../api/fetchDrugSearch';
import { BULK_DRUG_DIALOG, FROM_PRESCRIPTION_PAD, PAD_FIX_STRIP } from '../../../trackSource';
import { FlatMedObj } from './getFlatArray';
import { track } from '../../../../../tracking';

type Props = {
    drugs: FlatMedObj[];
    onSave: (drugs?: FlatMedObj[]) => void;
    focusDrugName: (elId: string) => void;
};

type State = {
    isDialogVisible: boolean;
    drugs: FlatMedObj[];
    matchingDrugs?: { [k: string]: CodedMedications[] };
};

export const BULK_ADD_DRUG_DIALOG = 'BULK_ADD_DRUG_DIALOG';

class AddDrugStrip extends PureComponent<Props, State> {
    state: State = {
        isDialogVisible: false,
        drugs: this.props.drugs,
    };

    componentDidMount(): void {
        track('FIX_DRUG_STRIP', { action: 'mount' });
    }

    showAddDrugDialog = (): void => {
        this.setState({ isDialogVisible: true, drugs: this.props.drugs });
        this.onPropertyChange();
        track('FIX_DRUG_STRIP', { action: 'SHOW_DIALOG' });
    };

    hideAddDrugDialog = (): void => {
        this.setState({ isDialogVisible: false });
        track('FIX_DRUG_STRIP', { action: 'HIDE_DIALOG' });
    };

    onSave = (): void => {
        this.props.onSave(this.state.drugs);
        this.hideAddDrugDialog();
    };

    onEditDrug = (drug: FlatMedObj): void => {
        const { focusDrugName } = this.props;

        this.hideAddDrugDialog();
        focusDrugName(drug.id);
    };

    onChange = (drug: FlatMedObj, matchingDrugs?: CodedMedications[], index?: number): void => {
        this.setState(
            {
                drugs: cloneAndUpsert(
                    { ...drug, track: { ...drug.track, label: BULK_DRUG_DIALOG } },
                    this.state.drugs,
                    index,
                ),
            },
            this.onPropertyChange,
        );
    };

    onPropertyChange = async (): Promise<void> => {
        const { drugs } = this.state;

        const res = await fetchDrugSearch(
            {
                medications: drugs
                    .filter((i) => !i.med_cat)
                    .map((drug) => ({
                        name: drug.name,
                        id: drug.id,
                        form: drug.dosage_form || drug.form,
                        unit: drug.dose?.unit,
                        uid: drug.dose?.unit,
                        generic_name: drug.generic_name,
                        generic_id: drug.generic_id,
                        manufacturer_name: drug.manufacturer_name,
                        manufacturer_id: drug.manufacturer_id,
                        frequency: drug.frequency,
                        duration: drug.duration,
                    })),
            },
            FROM_PRESCRIPTION_PAD,
        );

        this.setState({ matchingDrugs: res });
    };

    render(): ReactNode {
        const { isDialogVisible, drugs, matchingDrugs } = this.state;

        return (
            <div className="px-12 flex space-x-8 bg-darwin-status-warning-100 items-center">
                {WarningIcon('w-14 h-14')}
                <span>
                    <span className="font-700">
                        {drugs?.length} {getUnit('drug', drugs?.length.toString())} can cause
                        potential medication dispensing errors.{' '}
                    </span>
                    <span>Fill details below to ensure 100% patient safety.</span>
                </span>
                <Button
                    type="link"
                    onClick={this.showAddDrugDialog}
                    className={
                        'text-darwin-primary-500 text-14 font-700 hover:text-darwin-primary-500'
                    }
                >
                    Fix now
                </Button>
                <Modal
                    title={`Few medicines need attention (${drugs.length})`}
                    visible={isDialogVisible}
                    onOk={this.onSave}
                    onCancel={this.hideAddDrugDialog}
                    destroyOnClose
                    width={800}
                    okText="Save"
                    bodyStyle={{
                        overflow: 'scroll',
                        maxHeight: 'calc(100vh - 200px)',
                        // minHeight: 300,
                    }}
                    style={{ top: 20 }}
                    // className={BULK_ADD_DRUG_DIALOG}
                >
                    <p>
                        You have {drugs.length} drugs with medico-legal liability. Please add
                        missing details to safeguard.
                    </p>
                    <div className="space-y-12 mt-16">
                        {drugs.map((drug, index) => (
                            <div
                                key={drug.id}
                                className="border border border-darwin-neutral-200 rounded-8 overflow-hidden"
                            >
                                <AddDrugDialog
                                    drug={drug}
                                    title={drug.name}
                                    onEdit={() => this.onEditDrug(drug)}
                                    formAutoFocus={index === 0}
                                    onChange={(item, matchingDrugs) =>
                                        this.onChange(item, matchingDrugs, index)
                                    }
                                    matchingDrugs={matchingDrugs?.[drug.id]}
                                    labelForTracking={PAD_FIX_STRIP}
                                />
                            </div>
                        ))}
                    </div>
                </Modal>
            </div>
        );
    }
}

export default AddDrugStrip;
